import ReuseModal from "../../../components/fragment/modal/ReuseModal";
import {
  RazorBadgeTwo,
  RazorCopyText,
  RazorNumberFormat,
  RazorTextTrim,
} from "../../../components/razor-reuse";
import { StripeTransactionItem } from "../../../redux/type";
import { formatTime } from "../../../utils/Helper";

interface StripeTransactionModalProps {
  onClose?: () => void;
  visible?: boolean;
  details?: StripeTransactionItem | null;
}

// The session status enum the endpoint documents, mapped to a badge color.
const SESSION_STATUS_BADGE: Record<string, string> = {
  fulfillment_complete: "success",
  completed: "success",
  rejected: "failed",
  initialized: "pending",
  requires_payment: "pending",
  fulfillment_processing: "pending",
};

// business_transfer.status isn't a fixed enum we've been given, so bucket by
// keyword rather than an exact-match list that'd miss values we haven't seen.
function getBusinessTransferBadge(status?: string | null) {
  if (!status) return "";
  const normalized = status.toLowerCase();
  if (normalized.includes("fail") || normalized.includes("reject"))
    return "failed";
  if (
    normalized.includes("complete") ||
    normalized.includes("credited") ||
    normalized.includes("success")
  )
    return "success";
  return "pending";
}

function formatLabel(value?: string | null) {
  return value ? value.replace(/_/g, " ") : "---";
}

// Crypto amounts (e.g. USDC) aren't fiat currencies RazorNumberFormat knows a
// symbol for, so format them plainly instead of forcing a $ /£ style symbol.
function formatCryptoAmount(amount?: string | null, asset?: string | null) {
  if (amount == null) return "---";
  const numeric = Number(amount);
  const formatted = Number.isFinite(numeric)
    ? numeric.toLocaleString(undefined, { maximumFractionDigits: 6 })
    : amount;
  return `${formatted} ${asset ? asset.toUpperCase() : ""}`.trim();
}

function DetailRow({
  label,
  value,
  copyable,
}: {
  label: string;
  value?: React.ReactNode;
  copyable?: string | null;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-[16px] py-[6px]">
      <p className="text-[13px] text-[#758089] shrink-0">{label}</p>
      <div className="text-[13px] font-medium text-[#1E1E1E] text-right break-all">
        {copyable ? (
          <RazorCopyText
            textToCopy={copyable}
            reference={RazorTextTrim(copyable, 22)}
            outerCopy
          />
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[8px] bg-[#F4F4F5] px-[16px] py-[12px]">
      <p className="text-[12px] font-bold uppercase tracking-wide text-[#A1A1AA] mb-[4px]">
        {title}
      </p>
      <div className="divide-y divide-[#E4E4E7]">{children}</div>
    </div>
  );
}

export default function StripeTransactionModal({
  onClose,
  visible,
  details,
}: StripeTransactionModalProps) {
  const customerName = details?.customer
    ? `${details.customer.first_name || ""} ${details.customer.last_name || ""}`.trim() ||
      details.customer.email
    : "---";

  return (
    <ReuseModal
      title="Stripe Transaction Details"
      visible={visible}
      onClose={onClose}
      hideBtn
    >
      <div className="space-y-[16px] -mt-[8px]">
        <div className="flex items-center justify-between">
          <RazorCopyText
            textToCopy={details?.stripe_session_id || ""}
            reference={details?.stripe_session_id || "---"}
            outerCopy
          />
          <RazorBadgeTwo
            className={SESSION_STATUS_BADGE[details?.status || ""] || "pending"}
            text={formatLabel(details?.status)}
          />
        </div>

        <Section title="Customer">
          <DetailRow label="Name" value={customerName} />
          <DetailRow
            label="Email"
            value={details?.customer?.email}
            copyable={details?.customer?.email}
          />
        </Section>

        <Section title="Card Payment">
          <DetailRow
            label="Amount Charged"
            value={RazorNumberFormat(details?.card_payment?.amount, {
              symbol: details?.card_payment?.currency,
            })}
          />
          <DetailRow
            label="Transaction Fee"
            value={RazorNumberFormat(details?.card_payment?.transaction_fee, {
              symbol: details?.card_payment?.currency,
            })}
          />
          <DetailRow
            label="Network Fee"
            value={RazorNumberFormat(details?.card_payment?.network_fee, {
              symbol: details?.card_payment?.currency,
            })}
          />
          <DetailRow
            label="Total Charged"
            value={RazorNumberFormat(details?.card_payment?.total_charged, {
              symbol: details?.card_payment?.currency,
            })}
          />
          <DetailRow
            label="Payment Method"
            value={formatLabel(details?.card_payment?.payment_method)}
          />
          <DetailRow
            label="Status"
            value={formatLabel(details?.card_payment?.status)}
          />
          {details?.card_payment?.rejection_reason && (
            <DetailRow
              label="Rejection Reason"
              value={details.card_payment.rejection_reason}
            />
          )}
          {details?.card_payment?.rejected_at && (
            <DetailRow
              label="Rejected At"
              value={formatTime(details.card_payment.rejected_at)}
            />
          )}
        </Section>

        <Section title="Crypto Delivery">
          <DetailRow
            label="Amount"
            value={formatCryptoAmount(
              details?.crypto_delivery?.amount,
              details?.crypto_delivery?.asset,
            )}
          />
          <DetailRow
            label="Network"
            value={formatLabel(details?.crypto_delivery?.network)}
          />
          <DetailRow
            label="Wallet Address"
            value={details?.crypto_delivery?.wallet_address}
            copyable={details?.crypto_delivery?.wallet_address}
          />
          <DetailRow
            label="Transaction Hash"
            value={details?.crypto_delivery?.transaction_id}
            copyable={details?.crypto_delivery?.transaction_id}
          />
        </Section>

        <Section title="Business Transfer">
          <DetailRow
            label="Status"
            value={
              <RazorBadgeTwo
                className={getBusinessTransferBadge(
                  details?.business_transfer?.status,
                )}
                text={formatLabel(details?.business_transfer?.status)}
              />
            }
          />
          <DetailRow
            label="Amount"
            value={RazorNumberFormat(details?.business_transfer?.amount, {
              symbol: details?.business_transfer?.currency,
            })}
          />
          <DetailRow
            label="Customer Wallet ID"
            value={details?.business_transfer?.customer_wallet_id}
            copyable={details?.business_transfer?.customer_wallet_id}
          />
          <DetailRow
            label="Credited At"
            value={
              details?.business_transfer?.credited_at
                ? formatTime(details.business_transfer.credited_at)
                : null
            }
          />
          <DetailRow
            label="Bridge Transfer ID"
            value={details?.business_transfer?.bridge_transfer_id}
            copyable={details?.business_transfer?.bridge_transfer_id}
          />
          <DetailRow
            label="Bridge Transfer State"
            value={formatLabel(
              details?.business_transfer?.bridge_transfer_state,
            )}
          />
          <DetailRow
            label="Customer Ledger Ref."
            value={details?.business_transfer?.customer_ledger_reference}
            copyable={details?.business_transfer?.customer_ledger_reference}
          />
          <DetailRow
            label="Business Ledger Ref."
            value={details?.business_transfer?.business_ledger_reference}
            copyable={details?.business_transfer?.business_ledger_reference}
          />
        </Section>

        <div className="flex items-center justify-between text-[12px] text-[#A1A1AA] px-[4px]">
          <span>
            Created:{" "}
            {details?.created_at ? formatTime(details.created_at) : "---"}
          </span>
          <span>
            Updated:{" "}
            {details?.updated_at ? formatTime(details.updated_at) : "---"}
          </span>
        </div>
      </div>
    </ReuseModal>
  );
}
